import {
    formatGel,
    gelNumberOf,
    GEL_CURRENCY_FORMAT,
    parseGelToTetri,
    tetriToDecimalString,
} from '@/utils';
import type { ProductListRange } from '../productFilterTypes';
import { FILTERS_COPY, type FilterWords } from './filterLabels';

/** A range's unit owns crossings at the URL, API, input and display seams. */
export type RangeValueCodec = {
    read: (text: string) => number | null;
    write: (bound: number) => string;
    toRequest: (bound: number) => number;
    toField: (bound: number) => number;
    fromField: (value: number) => number | null;
    format: (bound: number) => string;
    fieldFormat: Intl.NumberFormatOptions | undefined;
};

export const MONEY_RANGE_VALUE: RangeValueCodec = {
    read: (text) => {
        const parsed = parseGelToTetri(text);
        return parsed.ok && Number.isSafeInteger(parsed.tetri)
            ? parsed.tetri
            : null;
    },
    write: tetriToDecimalString,
    toRequest: gelNumberOf,
    toField: gelNumberOf,
    fromField: (value) => {
        const parsed = parseGelToTetri(String(value));
        return parsed.ok ? parsed.tetri : null;
    },
    format: formatGel,
    fieldFormat: GEL_CURRENCY_FORMAT,
};

export const QUANTITY_RANGE_VALUE: RangeValueCodec = {
    read: (text) => {
        if (!/^\d+(?:\.\d+)?$/.test(text)) {
            return null;
        }
        const quantity = Number(text);
        return quantity <= Number.MAX_SAFE_INTEGER ? quantity : null;
    },
    write: String,
    toRequest: (bound) => bound,
    toField: (bound) => bound,
    fromField: (value) => value,
    format: String,
    fieldFormat: undefined,
};

/** The View translates the range message after its ends have been formatted. */
export const describeRangeValue = (
    { min, max }: ProductListRange,
    format: (bound: number) => string,
): FilterWords | null => {
    if (min !== null && max !== null) {
        return {
            key: `${FILTERS_COPY}.rangeBetween`,
            values: { min: format(min), max: format(max) },
        };
    }
    if (min !== null) {
        return {
            key: `${FILTERS_COPY}.rangeAtLeast`,
            values: { min: format(min) },
        };
    }
    if (max !== null) {
        return {
            key: `${FILTERS_COPY}.rangeUpTo`,
            values: { max: format(max) },
        };
    }
    return null;
};
